import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/AddOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import Stack from '@mui/material/Stack';
import CompanyFormDialog from '@/pages/masters/company/CompanyFormDialog';
import type { CompanyDto, OnboardCompanyRequest, UpdateCompanyRequest } from '@/types/organization';
import OnboardCompanyDialog from './OnboardCompanyDialog';

function extractErrorMessage(error: unknown): string | null {
  const axiosError = error as AxiosError<{ errors?: string[] }>;
  return axiosError.response?.data?.errors?.[0] ?? null;
}

export default function CompaniesConsolePage() {
  const { t } = useTranslation('companiesConsole');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => (await apiClient.get<CompanyDto[]>('/companies')).data,
  });

  const [onboardOpen, setOnboardOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<CompanyDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CompanyDto | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['companies'] });

  const onboardMutation = useMutation({
    mutationFn: (values: OnboardCompanyRequest) => apiClient.post('/companies/onboard', values),
    onSuccess: () => {
      invalidate();
      setOnboardOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: UpdateCompanyRequest) => apiClient.put(`/companies/${editingCompany!.id}`, values),
    onSuccess: () => {
      invalidate();
      setEditingCompany(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/companies/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<CompanyDto>[] = [
    { field: 'name', headerName: t('table.name') },
    { field: 'cin', headerName: t('table.cin'), render: (r) => r.cin ?? '—' },
    { field: 'gstin', headerName: t('table.gstin'), render: (r) => r.gstin ?? '—' },
    { field: 'currency', headerName: t('table.currency') },
    {
      field: 'branchCount',
      headerName: t('table.branches'),
      render: (r) => <Chip label={r.branchCount} size="small" />,
    },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'createdDate',
      headerName: t('table.createdDate'),
      render: (r) => new Date(r.createdDate).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Button size="small" startIcon={<TuneIcon fontSize="small" />} onClick={() => navigate(`/admin/companies/${r.id}/process-configuration`)}>
            {t('actions.processConfig')}
          </Button>
          <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => setEditingCompany(r)}>
            {t('actions.edit')}
          </Button>
          <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
            {t('actions.delete')}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOnboardOpen(true)}>
            {t('addCompany')}
          </Button>
        }
      />

      <DataTable columns={columns} rows={companies} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="companies" />

      <OnboardCompanyDialog
        open={onboardOpen}
        onClose={() => {
          setOnboardOpen(false);
          onboardMutation.reset();
        }}
        onSubmit={(values) => onboardMutation.mutate(values)}
        loading={onboardMutation.isPending}
        errorMessage={onboardMutation.isError ? (extractErrorMessage(onboardMutation.error) ?? t('onboard.genericError')) : null}
      />

      <CompanyFormDialog
        open={Boolean(editingCompany)}
        company={editingCompany}
        onClose={() => setEditingCompany(null)}
        onSubmit={(values) => updateMutation.mutate(values)}
        loading={updateMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteCompany.message', { name: confirmDelete?.name ?? '' })}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
